import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Person,
  Badge,
  Phone,
  Email,
  Lock,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import api from '../services/api';

interface Driver {
  id: number;
  name: string;
  email: string;
  phone?: string;
  driver_code: string;
  status: 'active' | 'inactive';
  color: string;
  created_at: string;
  updated_at: string;
}

const Drivers: React.FC = () => {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedDriverForPassword, setSelectedDriverForPassword] = useState<Driver | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    driver_code: '',
    status: 'active' as 'active' | 'inactive',
    color: '#2563EB',
    password: '',
  });

  useEffect(() => {
    fetchDrivers();
  }, []);

  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      // Filter to only show actual drivers (exclude admin and manager roles)
      const driversOnly = response.data.filter((user: any) => {
        // Exclude specific driver codes
        if (user.driver_code === 'ADMIN001' || user.driver_code === 'HIST') {
          return false;
        }
        // Include only drivers
        return user.role === 'driver' || // Only show users with driver role
          (!user.role && user.driver_code && user.driver_code !== ''); // Or users without role but with driver_code (legacy data)
      });
      setDrivers(driversOnly);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setFormData({
      name: driver.name,
      email: driver.email,
      phone: driver.phone || '',
      driver_code: driver.driver_code,
      status: driver.status,
      color: driver.color || '#2563EB',
      password: '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedDriver(null);
    setShowPassword(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      driver_code: '',
      status: 'active',
      color: '#2563EB',
      password: '',
    });
  };

  const handlePasswordClick = (driver: Driver) => {
    setSelectedDriverForPassword(driver);
    setNewPassword('');
    setShowNewPassword(false);
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setSelectedDriverForPassword(null);
    setNewPassword('');
    setShowNewPassword(false);
  };

  const handleChangePassword = async () => {
    if (!selectedDriverForPassword || !newPassword) return;
    
    try {
      await api.put(`/users/${selectedDriverForPassword.id}/password`, {
        password: newPassword,
      });
      alert('Password changed successfully!');
      handleClosePasswordDialog();
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    }
  };

  const handleSaveDriver = async () => {
    try {
      const dataToSend: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        driver_code: formData.driver_code,
        status: formData.status,
        color: formData.color,
        role: 'driver',
      };

      if (selectedDriver) {
        // Update existing driver (don't send password if empty)
        if (formData.password) {
          dataToSend.password = formData.password;
        }
        await api.put(`/users/${selectedDriver.id}`, dataToSend);
      } else {
        // Create new driver (password required for new driver)
        if (!formData.password) {
          alert('Password is required for new driver');
          return;
        }
        dataToSend.password = formData.password;
        await api.post('/users', dataToSend);
      }
      fetchDrivers();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving driver:', error);
      alert('Error saving driver. Please check all fields and try again.');
    }
  };

  const handleDeleteDriver = async (driverId: number) => {
    if (window.confirm('Are you sure you want to delete this driver?')) {
      try {
        await api.delete(`/users/${driverId}`);
        fetchDrivers();
      } catch (error) {
        console.error('Error deleting driver:', error);
      }
    }
  };

  const getStatusChip = (status: 'active' | 'inactive') => {
    return status === 'active' ? (
      <Chip label="Active" color="success" size="small" />
    ) : (
      <Chip label="Inactive" color="default" size="small" />
    );
  };

  if (loading) {
    return <Typography>Loading drivers...</Typography>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Active Drivers</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
          onClick={() => setEditDialogOpen(true)}
        >
          Add New Driver
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Driver Code</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Colour</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No drivers found
                  </TableCell>
                </TableRow>
              ) : (
                drivers.map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
                        {driver.name}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Badge sx={{ fontSize: 18, color: 'text.secondary' }} />
                        {driver.driver_code}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                        {driver.email}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {driver.phone && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                          {driver.phone}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          backgroundColor: driver.color || '#2563EB',
                          border: '2px solid rgba(0,0,0,0.15)',
                          display: 'inline-block',
                        }}
                        title={driver.color || '#2563EB'}
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(driver.status)}</TableCell>
                    <TableCell>
                      {new Date(driver.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(driver)}
                        color="primary"
                        title="Edit Driver"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handlePasswordClick(driver)}
                        color="secondary"
                        title="Change Password"
                      >
                        <Lock />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteDriver(driver.id)}
                        color="error"
                        title="Delete Driver"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedDriver ? 'Edit Driver' : 'Add New Driver'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Driver Code"
              value={formData.driver_code}
              onChange={(e) => setFormData({ ...formData, driver_code: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                label="Status"
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Map Marker Colour
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  style={{
                    width: 48,
                    height: 48,
                    border: 'none',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    padding: 0,
                    backgroundColor: 'transparent',
                  }}
                />
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    backgroundColor: formData.color,
                    border: '2px solid rgba(0,0,0,0.2)',
                  }}
                />
                <Typography variant="body2">{formData.color}</Typography>
              </Box>
            </Box>
            <TextField
              label={selectedDriver ? "New Password (leave blank to keep current)" : "Password"}
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required={!selectedDriver}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveDriver} 
            variant="contained"
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
          >
            {selectedDriver ? 'Save Changes' : 'Add Driver'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Change Password for {selectedDriverForPassword?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Cancel</Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
            disabled={!newPassword}
          >
            Change Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Drivers;